import { useEffect, useCallback, useState, useContext } from 'react';
import { FaFileDownload, FaRegStickyNote } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import ReactTooltip from 'react-tooltip';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AuthContext } from '../../../../actions/auth-context';
import {
  getSW3ThumbnailUrl,
  addCapturedPhotosToOrder,
  deleteCapturedPhotosOfOrder,
  downloadCapturedPhoto,
  downloadCapturedPhotosFromOrder,
  updateCapturedPhotosAttributes,
  updateCapturedPhotoObservation,
} from '../../../../actions/order.service';
import {
  expectedPhotosByProducts,
  PHOTO_SIZES,
  PHOTO_CLASSIFICATIONS,
} from '../../../../commons/order-checklist';
import { canViewCapturedPhotos } from '../../../../commons/photo-visibility';
import DeleteModal from '../../../../components/delete-modal';
// Reusa o visual do modal padrão (header escuro, footer centralizado).
import { Modal as ObservationModal } from '../../../../components/delete-modal/DeleteModal';
import S3Image from '../../../../components/s3-image';
import { InputFile } from './action-buttons/AddPhotosButton';
import { Container, Photo } from './EditedPhotos';

const CapturedPhotos = ({ order, setOrder }) => {
  const { setLoading, hasRoles } = useContext(AuthContext);

  const [thumbnails, setThumbnails] = useState([]);

  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);

  // Tamanho/classificação a aplicar nas selecionadas.
  const [assignSize, setAssignSize] = useState('');

  const [assignClassification, setAssignClassification] = useState('');

  // Observação individual em edição (id da foto sendo editada + texto do input).
  const [obsEditingId, setObsEditingId] = useState(null);

  const [obsText, setObsText] = useState('');

  // Fotos capturadas (brutas) anexadas pelo vendedor logo apos a criacao do
  // pedido. Sao de uso interno e nunca aparecem para o cliente final.
  const capturedPhotos = (order.photos || []).filter(
    (item) => item.photo_type === 'Capturada'
  );

  // FEAT-015: validação DURA — o select só oferece os tamanhos dos PRODUTOS do
  // pedido. Sem restrição quando o pedido tem 'Pacote' (conteúdo desconhecido)
  // ou nenhum produto com tamanho parametrizado (legado / só-fotolivro).
  const hasPackage = (order.products || []).some((p) => p.type === 'Pacote');

  const productSizes = [
    ...new Set((order.products || []).map((p) => p.photo_size).filter(Boolean)),
  ];

  const availableSizes =
    hasPackage || productSizes.length === 0
      ? PHOTO_SIZES
      : PHOTO_SIZES.filter((size) => productSizes.includes(size));

  // FEAT-015: quantidades ESPERADAS por classificação/tamanho (alerta VISUAL
  // "X de N" — não bloqueia estágio). Fonte única: commons/order-checklist
  // (inclui os ADICIONAIS de álbum, ex. 'Foto Adicional Fotolivro').
  const {
    album: expectedAlbumBySize,
    avulsa: expectedAvulsaBySize,
    albumTotal: expectedAlbumPhotos,
  } = expectedPhotosByProducts(order);

  const expectedAvulsaPhotos = Object.values(expectedAvulsaBySize).reduce(
    (sum, value) => sum + value,
    0
  );

  // Esperado de um subgrupo (tamanho) conforme a seção de classificação.
  const expectedForGroup = (sectionKey, size) => {
    if (sectionKey === 'Fotolivro') return expectedAlbumBySize[size] || null;
    if (sectionKey === 'Foto avulsa') return expectedAvulsaBySize[size] || null;
    return null;
  };

  // FEAT-015 UX: botões de upload por (tamanho × classificação) derivados dos
  // produtos do pedido — Fotolivro → foto do álbum; Revelação/Plotter → avulsa.
  // A foto já nasce com tamanho E classificação carimbados.
  const uploadButtons = [];

  (order.products || []).forEach((p) => {
    if (!p.photo_size) return;

    const classification = p.type === 'Fotolivro' ? 'Fotolivro' : 'Foto avulsa';

    const exists = uploadButtons.some(
      (b) => b.size === p.photo_size && b.classification === classification
    );

    if (!exists) uploadButtons.push({ size: p.photo_size, classification });
  });

  // Álbum primeiro, depois avulsas; dentro de cada grupo, ordem do rol.
  uploadButtons.sort(
    (a, b) =>
      (a.classification === 'Fotolivro' ? 0 : 1) -
        (b.classification === 'Fotolivro' ? 0 : 1) ||
      PHOTO_SIZES.indexOf(a.size) - PHOTO_SIZES.indexOf(b.size)
  );

  // Pode anexar/remover capturada: Vendedor, Administrador e Diretor (master),
  // somente com o pedido em 'Em inclusão'.
  const canManage = useCallback(
    () =>
      hasRoles(['master', 'administrator', 'seller']) &&
      order.order_status === 'Em inclusão',
    [order]
  );

  // OBSERVAÇÃO editável enquanto o pedido está no SETOR COMERCIAL: 'Em
  // inclusão' ou 'Financeiro negado' (ajuste 06/07 — ao voltar do financeiro,
  // o vendedor corrige/complementa as observações antes de reenviar).
  const canEditObservation = useCallback(
    () =>
      hasRoles(['master', 'administrator', 'seller']) &&
      ['Em inclusão', 'Financeiro negado'].includes(order.order_status),
    [order]
  );

  // Download das capturadas (pelo editor) so com o pedido 'Em edição'.
  // Master/Administrador podem em qualquer status (supervisao).
  const canDownload =
    hasRoles(['master', 'administrator']) || order.order_status === 'Em edição';

  // Ajuste 2 (30/06): veem master/admin/seller/editor/inspector; DEPOIS de EDITADAS
  // ('Editado' em diante) só master/admin/editor (inspetor inspeciona a editada, não
  // a capturada).
  const canView = canViewCapturedPhotos(hasRoles, order.order_status);

  const getLocalBlobThumbnail = useCallback(
    async (photo) => {
      // FEAT-011: presigned URL do S3 (antes baixava o blob pela API).
      const result = await getSW3ThumbnailUrl(order.id, photo.id);

      return {
        id: photo.id,
        blob: result,
        photo_size: photo.photo_size,
        photo_classification: photo.photo_classification,
        observation: photo.observation,
        created_at: photo.created_at,
      };
    },
    [order]
  );

  const prepareThumbnails = useCallback(async () => {
    if (capturedPhotos.length > 0) {
      setIsLoadingPhotos(true);

      const list = capturedPhotos.map((photo) => getLocalBlobThumbnail(photo));

      const result = await Promise.all(list);

      // Ordena pela data de UPLOAD (created_at), não pelo id (UUID aleatório).
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      setThumbnails(result);

      setIsLoadingPhotos(false);
    } else {
      setThumbnails([]);
      setIsLoadingPhotos(false);
    }
  }, [order]);

  useEffect(() => {
    prepareThumbnails();
  }, [order]);

  const onChangePhoto = useCallback(
    async (e, photoSize, photoClassification) => {
      e.preventDefault();

      setLoading(true);

      let files;

      if (e.dataTransfer) {
        files = e.dataTransfer.files;
      } else if (e.target) {
        files = e.target.files;
      }

      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        formData.append('photos', files[i]);
      }

      // FEAT-015 UX: upload pelo botão "add fotos/álbum {tamanho}" já carimba
      // tamanho E classificação (o backend valida contra os produtos).
      if (photoSize) formData.append('photo_size', photoSize);
      if (photoClassification)
        formData.append('photo_classification', photoClassification);

      const response = await addCapturedPhotosToOrder(order.id, formData);

      if (!response.error) {
        response.delivery_date = new Date(response.delivery_date);

        if (response.payment_first_date) {
          response.payment_first_date = new Date(
            response.payment_first_date.replace(/-/g, ',')
          );
        }

        setOrder(response);
      }

      setLoading(false);
    },
    [order]
  );

  const downloadOne = useCallback(
    async (photo_id) => {
      const photo = capturedPhotos.find((item) => item.id === photo_id);

      setLoading(true);

      await downloadCapturedPhoto(order.id, photo_id, photo?.file_name);

      setLoading(false);
    },
    [order, capturedPhotos]
  );

  const downloadAll = useCallback(async () => {
    setLoading(true);

    await downloadCapturedPhotosFromOrder(order.id, order.person?.full_name);

    setLoading(false);
  }, [order]);

  // Baixar as selecionadas (20/07): uma a uma, sequencial — mesma mecânica das
  // editadas/lâminas. Útil para pedidos grandes (baixar em partes).
  const downloadSelected = useCallback(async () => {
    setLoading(true);

    await selectedIds.reduce(async (prev, id) => {
      await prev;
      const photo = capturedPhotos.find((item) => item.id === id);
      return downloadCapturedPhoto(order.id, id, photo?.file_name);
    }, Promise.resolve());

    setLoading(false);
  }, [selectedIds, capturedPhotos, order]);

  const toggleSelected = useCallback((photo_id) => {
    setSelectedIds((state) =>
      state.includes(photo_id)
        ? state.filter((id) => id !== photo_id)
        : [...state, photo_id]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((state) =>
      state.length === capturedPhotos.length
        ? []
        : capturedPhotos.map((p) => p.id)
    );
  }, [capturedPhotos]);

  const removeSelected = useCallback(async () => {
    setLoading(true);

    const response = await deleteCapturedPhotosOfOrder(order.id, selectedIds);

    if (!response.error) {
      response.delivery_date = new Date(response.delivery_date);

      if (response.payment_first_date) {
        response.payment_first_date = new Date(
          response.payment_first_date.replace(/-/g, ',')
        );
      }

      setOrder(response);

      setSelectedIds([]);
    }

    setLoading(false);
  }, [selectedIds, order]);

  // Aplica tamanho e/ou classificação às fotos capturadas selecionadas.
  const applyAttributes = useCallback(async () => {
    if (!assignSize && !assignClassification) return;

    setLoading(true);

    const payload = { ids: selectedIds };
    if (assignSize) payload.photo_size = assignSize;
    if (assignClassification)
      payload.photo_classification = assignClassification;

    const response = await updateCapturedPhotosAttributes(order.id, payload);

    if (!response.error) {
      response.delivery_date = new Date(response.delivery_date);

      if (response.payment_first_date) {
        response.payment_first_date = new Date(
          response.payment_first_date.replace(/-/g, ',')
        );
      }

      setOrder(response);

      setSelectedIds([]);
    }

    setLoading(false);
  }, [order, selectedIds, assignSize, assignClassification]);

  // Abre o MODAL de observação de uma foto (carrega o texto atual).
  const openObservation = useCallback((photo) => {
    setObsEditingId(photo.id);
    setObsText(photo.observation || '');
  }, []);

  // Salva a observação individual da foto capturada (modal).
  const saveObservation = useCallback(async () => {
    if (!obsEditingId) return;

    setLoading(true);

    const response = await updateCapturedPhotoObservation(
      order.id,
      obsEditingId,
      obsText
    );

    if (!response.error) {
      response.delivery_date = new Date(response.delivery_date);

      if (response.payment_first_date) {
        response.payment_first_date = new Date(
          response.payment_first_date.replace(/-/g, ',')
        );
      }

      setOrder(response);

      setObsEditingId(null);
    }

    setLoading(false);
  }, [order, obsEditingId, obsText]);

  // Sem produtos no pedido não há o que capturar (nem como validar tamanhos):
  // a seção só aparece depois de incluir ao menos um produto.
  const hasProducts = (order.products || []).length > 0;

  if (!canView || !hasProducts) return null;

  // Seções visualmente distintas por classificação (FEAT-013).
  const CLASSIFICATION_SECTIONS = [
    { key: 'Fotolivro', label: 'Fotolivro' },
    { key: 'Foto avulsa', label: 'Foto avulsa' },
    { key: null, label: 'Não classificadas' },
  ];

  // FEAT-015 UX: dentro de cada seção, as fotos ficam agrupadas por TAMANHO
  // (na ordem do rol; sem tamanho por último).
  const groupBySize = (items) =>
    [...PHOTO_SIZES, null]
      .map((size) => ({
        size,
        label: size || 'Sem tamanho',
        photos: items.filter((t) =>
          size ? t.photo_size === size : !t.photo_size
        ),
      }))
      .filter((group) => group.photos.length > 0);

  const sectionItems = (section) =>
    thumbnails.filter((t) =>
      section.key
        ? t.photo_classification === section.key
        : !t.photo_classification
    );

  // Numeração sequencial na ORDEM DE EXIBIÇÃO (seções e grupos de tamanho em
  // sequência), para não aparecer fora de ordem.
  const orderedForDisplay = CLASSIFICATION_SECTIONS.flatMap((section) =>
    groupBySize(sectionItems(section)).flatMap((group) => group.photos)
  );
  const numberById = new Map(orderedForDisplay.map((t, i) => [t.id, i + 1]));

  const renderCard = (thumbnail) => {
    const number = numberById.get(thumbnail.id);

    return (
      <CapturedPhoto
        key={thumbnail.id}
        className="col-6 col-md-2"
        has_observation={!!thumbnail.observation}
      >
        <div className="card mt-2">
          <S3Image
            src={thumbnail.blob}
            alt="foto capturada"
            refreshUrl={() => getSW3ThumbnailUrl(order.id, thumbnail.id)}
          />
          <div className="bg-check">
            {/* Seleção também para quem BAIXA (20/07): o editor usa para
                baixar em partes um pedido com muitas fotos. */}
            {(canManage() || canDownload) && (
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={selectedIds.includes(thumbnail.id)}
                onChange={() => toggleSelected(thumbnail.id)}
                data-tip="Selecionar"
              />
            )}

            {canDownload && (
              <FaFileDownload
                size={22}
                color="#fff"
                className="me-2 photo-action"
                data-tip="Baixar foto original"
                onClick={() => downloadOne(thumbnail.id)}
              />
            )}

            {canEditObservation() && (
              <FaRegStickyNote
                size={20}
                color={thumbnail.observation ? '#ffba00' : '#fff'}
                className="me-2 photo-action"
                data-tip={thumbnail.observation || 'Adicionar observação'}
                data-bs-toggle="modal"
                data-bs-target="#modal-captured-observation"
                // NB: sem stopPropagation — o Bootstrap abre o modal escutando
                // o clique NO DOCUMENTO; barrar a propagação impedia de abrir.
                onClick={() => openObservation(thumbnail)}
              />
            )}

            {!canEditObservation() && thumbnail.observation && (
              <FaRegStickyNote
                size={20}
                color="#ffba00"
                className="me-2"
                data-tip={thumbnail.observation}
              />
            )}

            {thumbnail.photo_size && (
              <span
                className="badge fw-bold size-badge"
                style={{ fontSize: '12px' }}
              >
                {thumbnail.photo_size}
              </span>
            )}

            <div className="index">
              <span>{number}</span>
            </div>
          </div>
        </div>

        <ReactTooltip />
      </CapturedPhoto>
    );
  };

  return (
    <>
      <Container className="mt-5">
        <div className="text-center bg-dark">
          <h5 className="mt-2">FOTOS CAPTURADAS </h5>
        </div>

        <div className="container mt-2">
          <div className="row">
            <div className="col-12 col-md-8">
              {/* FEAT-015 UX: um botão de upload por TAMANHO × TIPO derivado dos
                  produtos do pedido — a foto já nasce com tamanho e classificação
                  carimbados. Sem tamanhos parametrizados (ex.: só Pacote), botão
                  genérico como antes. */}
              {canManage() && uploadButtons.length > 0 && (
                <div className="d-flex flex-wrap gap-2">
                  {uploadButtons.map((button) => (
                    <InputFile
                      key={`${button.classification}-${button.size}`}
                      type="button"
                      className={
                        button.classification === 'Fotolivro'
                          ? 'btn btn-primary'
                          : 'btn btn-secondary'
                      }
                    >
                      {button.classification === 'Fotolivro'
                        ? `add álbum ${button.size}`
                        : `add fotos ${button.size}`}
                      <input
                        type="file"
                        multiple="multiple"
                        onChange={(e) =>
                          onChangePhoto(e, button.size, button.classification)
                        }
                        accept="image/*"
                      />
                    </InputFile>
                  ))}
                </div>
              )}

              {canManage() && uploadButtons.length === 0 && (
                <InputFile
                  type="button"
                  className="btn btn-secondary btn-block"
                >
                  add fotos capturadas
                  <input
                    type="file"
                    name="captured_photos"
                    id="captured_photos"
                    multiple="multiple"
                    onChange={onChangePhoto}
                    accept="image/*"
                  />
                </InputFile>
              )}
            </div>

            <div className="col-12 col-md-4 text-end total-fotos">
              {canDownload && capturedPhotos.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary me-2"
                  onClick={() => downloadAll()}
                >
                  baixar capturadas <FaFileDownload className="mb-1" />
                </button>
              )}
              <p className="d-inline">
                Total de fotos{' '}
                <span className="badge bg-secondary">
                  {capturedPhotos.length}
                </span>
              </p>
            </div>
          </div>

          {/* Barra de seleção com BOTÕES evidentes (20/07): selecionar/
              desmarcar sempre visíveis; ações desabilitadas até haver seleção. */}
          {(canManage() || canDownload) && capturedPhotos.length > 0 && (
            <>
              <div className="row align-items-center mt-2">
                <div className="col-12 col-md-5">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => toggleSelectAll()}
                  >
                    {selectedIds.length === capturedPhotos.length
                      ? 'desmarcar todas'
                      : 'selecionar todas'}
                  </button>
                  {selectedIds.length > 0 && (
                    <span className="ms-2" style={{ fontSize: '13px' }}>
                      {selectedIds.length} selecionada(s)
                    </span>
                  )}
                </div>

                <div className="col-12 col-md-7 text-md-end mt-2 mt-md-0">
                  {/* Baixar selecionadas (20/07): editor baixa em partes um
                      pedido com muitas fotos. */}
                  {canDownload && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary me-2"
                      disabled={selectedIds.length === 0}
                      onClick={() => downloadSelected()}
                    >
                      baixar selecionadas ({selectedIds.length})
                    </button>
                  )}

                  {canManage() && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={selectedIds.length === 0}
                      data-bs-toggle={
                        selectedIds.length > 0 ? 'modal' : undefined
                      }
                      data-bs-target={
                        selectedIds.length > 0
                          ? '#modal-remove-captured-photos'
                          : undefined
                      }
                    >
                      remover selecionadas ({selectedIds.length})
                    </button>
                  )}
                </div>
              </div>

              {canManage() && selectedIds.length > 0 && (
                <div className="row align-items-center mt-2">
                  <div className="col-12 d-flex flex-wrap gap-2 align-items-center">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={assignSize}
                      onChange={(e) => setAssignSize(e.target.value)}
                    >
                      <option value="">Tamanho...</option>
                      {availableSizes.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>

                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={assignClassification}
                      onChange={(e) => setAssignClassification(e.target.value)}
                    >
                      <option value="">Classificação...</option>
                      {PHOTO_CLASSIFICATIONS.map((classification) => (
                        <option key={classification} value={classification}>
                          {classification}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="btn btn-sm btn-primary text-nowrap"
                      disabled={!assignSize && !assignClassification}
                      onClick={() => applyAttributes()}
                    >
                      aplicar às selecionadas ({selectedIds.length})
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {isLoadingPhotos && (
            <p className="text-center mt-3">Carregando fotos do S3...</p>
          )}

          {!isLoadingPhotos && capturedPhotos.length === 0 && (
            <div className="alert alert-warning text-center mt-2" role="alert">
              <b>Atenção!</b> Nenhuma foto capturada anexada a este pedido.
            </div>
          )}

          {!isLoadingPhotos &&
            capturedPhotos.length > 0 &&
            CLASSIFICATION_SECTIONS.map((section) => {
              const items = sectionItems(section);

              if (items.length === 0) return null;

              return (
                <div key={section.label} style={{ clear: 'both' }}>
                  <div
                    className="section-title"
                    style={{
                      position: 'relative',
                      zIndex: 5,
                      width: '100%',
                      display: 'block',
                      backgroundColor: '#f2f2f2',
                      borderLeft: '4px solid #ffba00',
                      padding: '8px 10px',
                      margin: '40px 0 14px',
                      fontWeight: 'bold',
                      fontSize: '15px',
                      color: '#464646',
                      textTransform: 'none',
                    }}
                  >
                    {section.label}{' '}
                    <span style={{ fontWeight: 'normal', color: '#888' }}>
                      ({items.length})
                    </span>
                    {/* Alerta visual: X de N fotos (não bloqueia). */}
                    {section.key === 'Fotolivro' && expectedAlbumPhotos > 0 && (
                      <span
                        style={{
                          marginLeft: '10px',
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor:
                            items.length >= expectedAlbumPhotos
                              ? '#c8e6c9'
                              : '#fff3cd',
                          color:
                            items.length >= expectedAlbumPhotos
                              ? '#1b5e20'
                              : '#856404',
                        }}
                      >
                        {items.length} de {expectedAlbumPhotos} fotos do álbum
                      </span>
                    )}
                    {section.key === 'Foto avulsa' && expectedAvulsaPhotos > 0 && (
                      <span
                        style={{
                          marginLeft: '10px',
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor:
                            items.length >= expectedAvulsaPhotos
                              ? '#c8e6c9'
                              : '#fff3cd',
                          color:
                            items.length >= expectedAvulsaPhotos
                              ? '#1b5e20'
                              : '#856404',
                        }}
                      >
                        {items.length} de {expectedAvulsaPhotos} fotos avulsas
                      </span>
                    )}
                  </div>

                  {/* FEAT-015 UX: subgrupos por TAMANHO dentro da seção, com o
                      contador (enviadas/esperadas) quando o produto define a
                      quantidade — verde quando bate, âmbar quando não. */}
                  {groupBySize(items).map((group) => {
                    const expected = expectedForGroup(section.key, group.size);

                    return (
                      <div key={group.label}>
                        {/* Linha separadora ANTES do grupo (acima do selo). */}
                        <hr
                          style={{
                            margin: '22px 0 10px',
                            borderTop: '1px solid #d0d0d0',
                            opacity: 1,
                          }}
                        />

                        <div
                          style={{
                            margin: '0 0 6px',
                            paddingLeft: '10px',
                            textTransform: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          {/* Medida em destaque — cinza (o amarelo confundia
                              com os badges das próprias fotos). */}
                          <span
                            style={{
                              backgroundColor: '#464646',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '13px',
                              padding: '2px 14px',
                              borderRadius: '12px',
                            }}
                          >
                            {group.label}
                          </span>
                          {expected ? (
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: '13px',
                                color:
                                  group.photos.length >= expected
                                    ? '#1b5e20'
                                    : '#856404',
                              }}
                            >
                              {group.photos.length}/{expected}
                            </span>
                          ) : (
                            <span style={{ fontSize: '13px', color: '#888' }}>
                              ({group.photos.length})
                            </span>
                          )}
                        </div>

                        <div className="row">
                          {group.photos.map((t) => renderCard(t))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Linha de fechamento após o último grupo da seção. */}
                  <hr
                    style={{
                      margin: '22px 0 0',
                      borderTop: '1px solid #d0d0d0',
                      opacity: 1,
                    }}
                  />
                </div>
              );
            })}
        </div>
      </Container>

      <DeleteModal
        id_modal="modal-remove-captured-photos"
        item_delete={{ description: 'fotos capturadas selecionadas' }}
        deleteItem={() => removeSelected()}
      />

      {/* Modal de OBSERVAÇÃO da foto capturada (pedido do dono 05/07: mais
          visível que o editor inline embaixo do card). */}
      <ObservationModal>
        {/* data-bs-backdrop="false": padrão do app para modal COM campo de
            texto — o backdrop cobre o modal nesta árvore e rouba o foco do
            campo (mesma solução do SendReworkModal). */}
        <div
          className="modal"
          tabIndex="-1"
          id="modal-captured-observation"
          data-bs-backdrop="false"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title col-11">OBSERVAÇÃO DA FOTO</h5>
                <MdClose size={30} data-bs-dismiss="modal" type="button" />
              </div>
              <div className="bg-modal">
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    rows={4}
                    maxLength={500}
                    value={obsText}
                    onChange={(e) => setObsText(e.target.value)}
                    placeholder="Escreva a observação desta foto (ex.: retoque, corte, cor)..."
                  />
                  <small className="text-muted d-block text-end mt-1">
                    {obsText.length}/500
                  </small>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary col-4"
                    data-bs-dismiss="modal"
                  >
                    cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary col-4"
                    data-bs-dismiss="modal"
                    onClick={() => saveObservation()}
                  >
                    salvar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ObservationModal>
    </>
  );
};

CapturedPhotos.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string,
    order_status: PropTypes.string,
    photos: PropTypes.arrayOf(Object),
    products: PropTypes.arrayOf(Object),
    person: PropTypes.shape({
      full_name: PropTypes.string,
    }),
  }).isRequired,
  setOrder: PropTypes.func.isRequired,
};

export default CapturedPhotos;

// Variante das capturadas: mantem a proporcao original da foto (retrato/paisagem),
// sem o corte do object-fit: cover usado nas demais galerias. Foto COM
// observacao ganha borda amarela para chamar atencao (pedido do dono 05/07) —
// o texto aparece no tooltip do icone de observacao.
const CapturedPhoto = styled(Photo)`
  img {
    object-fit: contain;
    background-color: #f5f5f5;
    border: ${(props) =>
      props.has_observation ? '4px solid #ffba00' : 'none'};
  }

  /* O Container herdado tem ".badge { background: #464646 !important }". Esta regra
     mais especifica vence e deixa o tag de tamanho legivel (amarelo de marca). */
  .bg-check .size-badge {
    background-color: #ffba00 !important;
    color: #212529 !important;
  }

  .obs-editor textarea {
    text-transform: none;
    font-size: 12px;
  }
`;
